import type { Ref } from 'vue';
import type * as openapi from '@/helpers/api/types/openapiOverrides';
import type {
  BookingStop,
  JobBookingCustomer,
  JobBookingRefConfig,
  StopNumber,
} from '@/models/advancedJobBooking';

import { computed, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { addDays, format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

import { timeZoneDefault } from '@/utils/PortalConstants';
import { useCustomerDetailsStore } from '@/stores/useCustomerDetailsStore';
import { useJobEditAddressBookStore } from '@/stores/useJobEditAddressBookStore';
import {
  createEmptyCustomer,
  createEmptyStop,
} from '@/models/advancedJobBooking';

export type RefConfigTuple = [
  JobBookingRefConfig,
  JobBookingRefConfig,
  JobBookingRefConfig,
];

export interface JobBookingCustomerDataComposable {
  customer: Ref<JobBookingCustomer>;
  refConfigs: Ref<RefConfigTuple>;
  addressItems: Ref<openapi.AddressGETOpenapi[]>;
  addressLoading: Ref<boolean>;
}

const DEFAULT_REF_CONFIGS: RefConfigTuple = [
  { label: 'Reference 1', required: false },
  { label: 'Reference 2', required: false },
  { label: 'Reference 3', required: false },
];

/** Next business day (skipping weekends) formatted as `YYYY-MM-DD`. */
export function nextBusinessDayISO(): string {
  const nowInTZ = toZonedTime(new Date(), timeZoneDefault);
  const tomorrowDay = addDays(nowInTZ, 1).getDay();
  let offset = 1;
  if (tomorrowDay === 6) {
    offset = 3; // Saturday → Monday
  } else if (tomorrowDay === 0) {
    offset = 2; // Sunday → Monday
  }
  return format(addDays(nowInTZ, offset), 'yyyy-MM-dd');
}

export function createBookingStop(number: StopNumber): BookingStop {
  const businessDay = nextBusinessDayISO();
  return {
    ...createEmptyStop(number),
    start_date: businessDay,
    end_date: businessDay,
    link_dates: true,
  };
}

export default function useJobBookingCustomerData(): JobBookingCustomerDataComposable {
  const addressBookStore = useJobEditAddressBookStore();
  const customerDetailsStore = useCustomerDetailsStore();

  const { items: addressItems, loading: addressLoading } =
    storeToRefs(addressBookStore);

  const customer = ref<JobBookingCustomer>(createEmptyCustomer());

  const refConfigs = computed<RefConfigTuple>(() => {
    const code = customer.value.customer_code;
    if (!code) return DEFAULT_REF_CONFIGS;
    const details = customerDetailsStore.getCachedCustomerDetails(code);
    if (!details) return DEFAULT_REF_CONFIGS;
    return [
      {
        label: details.ref1_prompt ?? 'Reference 1',
        required: !!details.ref1_required,
      },
      {
        label: details.ref2_prompt ?? 'Reference 2',
        required: !!details.ref2_required,
      },
      {
        label: details.ref3_prompt ?? 'Reference 3',
        required: !!details.ref3_required,
      },
    ];
  });

  // Prefetch the customer's address book and ref-field config when it changes.
  watch(
    () => customer.value.customer_code,
    (code) => {
      if (!code) return;
      void addressBookStore.fetch(code).catch(() => undefined);
      void customerDetailsStore.getCustomerDetails(code).catch(() => undefined);
    },
    { immediate: true },
  );

  return { customer, refConfigs, addressItems, addressLoading };
}
